import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Card,
  CardContent,
  Avatar,
  Grid,
  Chip,
  Typography,
} from '@mui/material';
import {
  RecordVoiceOver as RecordVoiceOverIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Quiz as QuizIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { createSpeaking, getSpeakingById, updateSpeaking } from '../../api/speakingApi';
import { getAllInstructors } from '../../api/instructorApi';
import { getAllExams } from '../../api/examApi';
import { useToast } from '../../contexts/ToastContext';
import { FormPageLayout } from '../../components/templates';
import ModernButton from '../../components/common/ModernButton';
import ModernTextField from '../../components/common/ModernTextField';

const SpeakingForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [speaking, setSpeaking] = useState({
    startsAt: '',
    endsAt: '',
    timeZone: '',
    instructorId: '',
    instructorComment: '',
    examId: '',
    isReserved: false,
  });

  const [instructors, setInstructors] = useState([]);
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [instructorsRes, examsRes] = await Promise.all([
          getAllInstructors(),
          getAllExams(),
        ]);
        setInstructors(instructorsRes.data || []);
        setExams(examsRes.data || []);

        if (id) {
          const { data } = await getSpeakingById(id);
          setSpeaking({ ...data, Score: data.Score?.toString() || '' });
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSpeaking((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (id) {
        await updateSpeaking(id, speaking);
        showToast('Speaking session updated successfully!', 'success');
      } else {
        await createSpeaking(speaking);
        showToast('Speaking session created successfully!', 'success');
      }
      navigate('/speakings');
    } catch (error) {
      console.error('Failed to save speaking:', error);
      showToast('An error occurred. Please try again.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormPageLayout
      title={id ? 'Edit Speaking Session' : 'Create Speaking Session'}
      subtitle={id ? 'Update speaking session details' : 'Set up a new speaking session'}
      icon={<RecordVoiceOverIcon />}
      loading={loading}
      backPath="/speakings"
      backLabel="Back to Sessions"
      maxWidth="xl"
    >
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card sx={{ mb: 3, borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: 'info.main', width: 32, height: 32, mr: 2 }}>
                    <ScheduleIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Session Timing
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Start Time"
                      name="startsAt"
                      type="datetime-local"
                      value={speaking.startsAt}
                      onChange={handleChange}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="End Time"
                      name="endsAt"
                      type="datetime-local"
                      value={speaking.endsAt}
                      onChange={handleChange}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <ModernTextField
                      label="Time Zone"
                      name="timeZone"
                      value={speaking.timeZone}
                      onChange={handleChange}
                      fullWidth
                      placeholder="e.g., UTC+0, EST, PST"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ mb: 3, borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: 'warning.main', width: 32, height: 32, mr: 2 }}>
                    <PersonIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Instructor Assignment
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Instructor</InputLabel>
                      <Select
                        name="instructorId"
                        value={speaking.instructorId}
                        onChange={handleChange}
                        label="Instructor"
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="">Select Instructor</MenuItem>
                        {instructors.map((instructor) => (
                          <MenuItem key={instructor._id} value={instructor._id}>
                            {instructor.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <ModernTextField
                      label="Instructor Comment"
                      name="instructorComment"
                      value={speaking.instructorComment}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Add any notes for the instructor..."
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: 'success.main', width: 32, height: 32, mr: 2 }}>
                    <QuizIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Exam Assignment
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <FormControl fullWidth required>
                      <InputLabel>Exam</InputLabel>
                      <Select
                        name="examId"
                        value={speaking.examId}
                        onChange={handleChange}
                        label="Exam"
                        sx={{ borderRadius: 2 }}
                      >
                        <MenuItem value="">Select Exam</MenuItem>
                        {exams.map((exam) => (
                          <MenuItem key={exam._id} value={exam._id}>
                            {exam.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="isReserved"
                          checked={speaking.isReserved}
                          onChange={handleChange}
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={speaking.isReserved ? 'Reserved' : 'Available'}
                            color={speaking.isReserved ? 'warning' : 'success'}
                            size="small"
                            variant="outlined"
                          />
                          <Typography variant="body2" color="text.secondary">
                            Mark this session as reserved
                          </Typography>
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card sx={{ mb: 3, borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: 'info.main', width: 32, height: 32, mr: 2 }}>
                    <InfoIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Session Preview
                  </Typography>
                </Box>
                <Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Instructor
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {instructors.find((i) => i._id === speaking.instructorId)?.name || 'Not selected'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Exam
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {exams.find((e) => e._id === speaking.examId)?.name || 'Not selected'}
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                      Status
                    </Typography>
                    <Chip
                      label={speaking.isReserved ? 'Reserved' : 'Available'}
                      color={speaking.isReserved ? 'warning' : 'success'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                  {speaking.startsAt && speaking.endsAt && (
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                        Duration
                      </Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {Math.round((new Date(speaking.endsAt) - new Date(speaking.startsAt)) / (1000 * 60))} minutes
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            <Card sx={{ borderRadius: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <ModernButton
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  fullWidth
                  disabled={saving}
                >
                  {saving ? 'Saving...' : id ? 'Update Session' : 'Create Session'}
                </ModernButton>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </form>
    </FormPageLayout>
  );
};

export default SpeakingForm;
